/**
 * CardOfTheDayBanner — live remaining-count + countdown for the
 * Scratch Card 200× daily cap.
 *
 * Behavior
 *   Polls GET /api/games/scratch-card/card-of-the-day every 30 s.
 *   Between polls, decrements `seconds_until_reset` locally with a
 *   1-second interval so the countdown ticks smoothly. Countdown
 *   snaps back into sync on the next poll (server is the source of
 *   truth for jackpot count too).
 *
 * States
 *   1. `cap_enabled === false`      → renders NOTHING (null). The
 *      Scratch Card page falls back to its normal prize-teaser row.
 *   2. remaining > 0                → gold rail: "Today's 200×
 *      Jackpots: {remaining} of {cap} remaining · Resets in {Xh Ym}".
 *   3. remaining === 0              → red rail: "All 200× jackpots
 *      claimed today. Resets in {Xh Ym}. Come back tomorrow!".
 *
 * Failure mode
 *   If the endpoint errors, banner renders nothing (silent fallback
 *   so a transient 500 doesn't nag the player mid-scratch). The
 *   normal prize teaser still renders below.
 */
import { useEffect, useRef, useState } from "react";
import { Clock, Sparkles } from "lucide-react";

import api from "../lib/api";

const POLL_MS = 30_000;
const TICK_MS = 1_000;

function fmtCountdown(seconds) {
  const s = Math.max(0, Math.floor(seconds || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  // Compact "Xh Ym" — hides hours once past the last hour of the day
  // so it doesn't look weird ("0h 4m" → "4m").
  if (h <= 0) return `${m}m`;
  return `${h}h ${m}m`;
}

export default function CardOfTheDayBanner({ className = "" }) {
  const [state, setState] = useState(null);   // full server snapshot
  const [remainingSec, setRemainingSec] = useState(0);
  const tickRef = useRef(null);

  // Polling — hydrate + refetch every POLL_MS. Cleanup cancels the
  // in-flight timer on unmount.
  useEffect(() => {
    let alive = true;
    let pollTimer = null;
    const load = async () => {
      try {
        const { data } = await api.get("/games/scratch-card/card-of-the-day");
        if (!alive) return;
        setState(data);
        setRemainingSec(Number(data?.seconds_until_reset || 0));
      } catch {
        // Silent — see module docstring.
      }
    };
    load();
    pollTimer = setInterval(load, POLL_MS);
    return () => {
      alive = false;
      if (pollTimer) clearInterval(pollTimer);
    };
  }, []);

  // Local countdown tick — decrements every second so the reset time
  // feels live without hammering the API.
  useEffect(() => {
    if (tickRef.current) clearInterval(tickRef.current);
    tickRef.current = setInterval(() => {
      setRemainingSec((v) => (v > 0 ? v - 1 : 0));
    }, TICK_MS);
    return () => { if (tickRef.current) clearInterval(tickRef.current); };
  }, []);

  // State 1 — cap disabled: render nothing.
  if (!state || state.cap_enabled === false) return null;

  const remaining = Math.max(0, Number(state.jackpot_remaining || 0));
  const cap = Math.max(1, Number(state.jackpot_cap || 1));
  const countdown = fmtCountdown(remainingSec);
  const exhausted = remaining <= 0;

  // State 3 — zero left today.
  if (exhausted) {
    return (
      <div
        data-testid="card-of-the-day-banner"
        data-state="exhausted"
        className={
          "w-full max-w-[360px] rounded-lg border px-3 py-2 flex items-center gap-2 " +
          "border-danger/60 bg-danger/10 text-danger " +
          className
        }
      >
        <Clock className="w-3.5 h-3.5 shrink-0" />
        <div className="flex-1 text-[11px] leading-tight">
          <div className="font-semibold tracking-[0.15em] uppercase text-[10px]">
            All 200× jackpots claimed today
          </div>
          <div className="text-[10px] text-danger/80 mt-0.5">
            Resets in <span data-testid="card-of-the-day-countdown" className="font-mono">{countdown}</span>. Come back tomorrow!
          </div>
        </div>
      </div>
    );
  }

  // State 2 — remaining > 0.
  return (
    <div
      data-testid="card-of-the-day-banner"
      data-state="remaining"
      className={
        "w-full max-w-[360px] rounded-lg border px-3 py-2 flex items-center gap-2 " +
        "border-gold/60 bg-gold/10 text-gold " +
        "shadow-[0_0_18px_rgba(212,161,58,0.25)] " +
        className
      }
    >
      <Sparkles className="w-3.5 h-3.5 shrink-0" />
      <div className="flex-1 text-[11px] leading-tight">
        <div className="font-semibold tracking-[0.15em] uppercase text-[10px]">
          Today's 200× jackpots
        </div>
        <div className="text-[10px] text-gold/85 mt-0.5">
          <span data-testid="card-of-the-day-remaining" className="font-mono font-semibold">{remaining}</span>
          {" "}of{" "}
          <span className="font-mono">{cap}</span>
          {" "}remaining · Resets in{" "}
          <span data-testid="card-of-the-day-countdown" className="font-mono">{countdown}</span>
        </div>
      </div>
    </div>
  );
}
